import { useEffect, useMemo, useState } from 'react';
import { loadCommissions, createMonthlyInvoice } from '../../lib/commissionsService';
import {
  tenantCommissions,
  commissionTotals,
  type TenantCommissionRaw,
} from '../../lib/commissions';
import { euros } from '../../lib/performance';

/**
 * « Mes revenus » (super admin) — commissions 0,99 €/commande en TEMPS RÉEL :
 *  - ENCAISSÉ   : payé en ligne (Stripe), déjà sur le compte,
 *  - À RECOUVRER : réglé sur place, à facturer au resto en fin de mois,
 *  par jour / semaine / mois, total réseau + détail par resto.
 */
const PERIODS: { id: 'jour' | 'semaine' | 'mois'; label: string; days: number }[] = [
  { id: 'jour', label: "Aujourd'hui", days: 1 },
  { id: 'semaine', label: '7 jours', days: 7 },
  { id: 'mois', label: '30 jours', days: 30 },
];

export default function CommissionsPanel() {
  const [periodId, setPeriodId] = useState<'jour' | 'semaine' | 'mois'>('mois');
  const [rows, setRows] = useState<TenantCommissionRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceBusy, setInvoiceBusy] = useState<string | null>(null);
  const [invoiceMsg, setInvoiceMsg] = useState<Record<string, string>>({});

  const now = new Date();
  const genInvoice = async (slug: string) => {
    setInvoiceBusy(slug);
    const res = await createMonthlyInvoice(slug, now.getFullYear(), now.getMonth() + 1);
    setInvoiceMsg((m) => ({ ...m, [slug]: res.message }));
    setInvoiceBusy(null);
  };

  const days = PERIODS.find((p) => p.id === periodId)?.days ?? 30;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCommissions(days).then((res) => {
      if (cancelled) return;
      if (res.ok) { setRows(res.tenants); setError(null); }
      else setError(res.error ?? 'Chargement impossible.');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [days]);

  const totals = useMemo(() => commissionTotals(rows), [rows]);
  const perTenant = useMemo(() => tenantCommissions(rows), [rows]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">💶 Mes revenus — commissions 0,99 €/commande</div>
          <h3 className="text-xl font-black text-slate-900">{euros(totals.totalCents)} <span className="text-sm font-semibold text-slate-500">· {totals.totalOrders} commande{totals.totalOrders > 1 ? 's' : ''}</span></h3>
        </div>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {PERIODS.map((p) => (
            <button key={p.id} onClick={() => setPeriodId(p.id)}
              className={`rounded-md px-3 py-1 text-sm font-semibold ${periodId === p.id ? 'bg-white text-slate-900 shadow' : 'text-slate-600 hover:text-slate-900'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div>
      ) : loading ? (
        <div className="py-6 text-center text-sm text-slate-400">Chargement…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs font-bold uppercase text-emerald-700">✅ Encaissé (Stripe)</div>
              <div className="mt-1 text-2xl font-black text-emerald-800">{euros(totals.encaisseCents)}</div>
              <div className="text-xs text-emerald-700/80">{totals.encaisseOrders} cmd en ligne · déjà sur votre compte</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-xs font-bold uppercase text-amber-700">🧾 À recouvrer (sur place)</div>
              <div className="mt-1 text-2xl font-black text-amber-800">{euros(totals.recouvrerCents)}</div>
              <div className="text-xs text-amber-700/80">{totals.recouvrerOrders} cmd réglées sur place · à facturer en fin de mois</div>
            </div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="px-2 py-2">Restaurant</th>
                  <th className="px-2 py-2 text-right">Encaissé</th>
                  <th className="px-2 py-2 text-right">À recouvrer</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2 text-right">Facture du mois</th>
                </tr>
              </thead>
              <tbody>
                {perTenant.map((t) => (
                  <tr key={t.slug} className="border-b border-slate-100">
                    <td className="px-2 py-2 font-semibold text-slate-700">{t.name}</td>
                    <td className="px-2 py-2 text-right text-emerald-700">{euros(t.encaisseCents)}</td>
                    <td className="px-2 py-2 text-right text-amber-700">{euros(t.recouvrerCents)}</td>
                    <td className="px-2 py-2 text-right font-bold text-slate-900">{euros(t.totalCents)}</td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => genInvoice(t.slug)} disabled={invoiceBusy === t.slug}
                        className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
                        {invoiceBusy === t.slug ? '…' : '🧾 Générer'}
                      </button>
                      {invoiceMsg[t.slug] && <div className="mt-1 text-[11px] text-slate-500">{invoiceMsg[t.slug]}</div>}
                    </td>
                  </tr>
                ))}
                {perTenant.length === 0 && (
                  <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400">Aucune commande sur la période.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
